import {
  Autocomplete,
  TextField,
  CircularProgress,
  Checkbox,
  Backdrop,
} from "@mui/material";
import CustomTable from "../../../components/table";
import {
  fetchCities,
  fetchCounties,
  fetchDevices,
  fetchLocations,
  fetchPartners,
  fetchTags,
} from "../../../services/loop";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import useDebounce from "../../../hooks/debounce";
import PopSnackbar from "../../../components/snackbar";

const styles = {
  select: {
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
    },
    "& .MuiInputLabel-root": { color: "#555" },
    "&.Mui-disabled .MuiOutlinedInput-root": { backgroundColor: "#f0f0f0" },
    "& .MuiSelect-outlined": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
    },
  },
};

function FilterAutocomplete({
  label,
  options,
  onChange,
  value,
  getOptionLabel,
  loading,
  multiple = true,
  disabled = false,
}) {
  return (
    <Autocomplete
      style={{ opacity: disabled ? 0.5 : 1 }}
      disabled={disabled}
      sx={styles.select}
      multiple={multiple}
      options={options}
      value={value}
      onChange={onChange}
      getOptionLabel={getOptionLabel}
      renderInput={(params) => (
        <>
          <TextField
            {...params}
            label={loading ? <CircularProgress size={20} /> : label}
          />
        </>
      )}
    />
  );
}

const EditLoopStep = forwardRef(
  (
    {
      data,
      campaignData,
      onChange,
      goBack,
      onSubmitAll,
      loadingbtn,
      handleStatus,
    },
    ref
  ) => {
    const {
      selectedDevices: initialSelectedDevices = [],
      filters: initialFilters = {},
      timeInterval: initialTimeInterval = 60,
    } = data;

    const columns = [
      {
        field: "selected_devices",
        headerName: "Selected",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => (
          <Checkbox
            disabled={loadingbtn}
            style={{ opacity: loadingbtn ? 0.5 : 1 }}
            color="black"
            checked={selectedDevices.some(
              (device) => device.device_id === params.row.device_id
            )}
            onChange={(event) =>
              handleDeviceSelect(
                params.row.device_id,
                params.row.name,
                event.target.checked
              )
            }
          />
        ),
        width: 75,
      },
      {
        field: "device_id",
        headerName: "ID Player",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => {
          return params.row.device_id;
        },
      },
      {
        field: "device_name",
        headerName: "Nume Player",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => {
          return params.row.name;
        },
      },
      {
        field: "partner",
        headerName: "Partener",
        sortable: false,
        headerAlign: "center",
        renderCell: (params) => {
          return params.row?.location?.partner?.name;
        },
      },
      {
        field: "location",
        headerName: "Locatie",
        sortable: false,
        headerAlign: "center",
        renderCell: (params) => {
          return params.row?.location?.name;
        },
      },
      {
        field: "county",
        headerName: "Judet",
        sortable: false,
        headerAlign: "center",
        renderCell: (params) => {
          return params.row?.location?.county;
        },
      },
      {
        field: "city",
        headerName: "Oras",
        sortable: false,
        headerAlign: "center",
        renderCell: (params) => {
          return params.row?.location?.city;
        },
      },
    ];

    const [devices, setDevices] = useState([]);
    const [partners, setPartners] = useState([]);
    const [tags, setTags] = useState([]);
    const [hierarchicalData, setHierarchicalData] = useState({
      counties: [],
      cities: {},
      locations: {},
    });

    const [loadingFilters, setLoadingFilters] = useState({
      partners: false,
      counties: false,
      cities: false,
      locations: false,
      tags: false,
    });

    const [loading, setLoading] = useState(true);

    const [selectedDevices, setSelectedDevices] = useState(
      initialSelectedDevices
    );

    const [timeInterval, setTimeInterval] = useState(initialTimeInterval);

    const [filters, setFilters] = useState(initialFilters);

    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    const [inputText, setInputText] = useState("");
    const debouncedSearchTerm = useDebounce(inputText, 500);

    const [toastStatus, setToastStatus] = useState(false);
    const [toastDescription, setToastDescription] = useState("");

    const [loopPreviewLoading, setLoopPreviewLoading] = useState(false);

    const hasNetworkSelected = filters.networks && filters.networks.length > 0;
    const hasPartnerSelected = filters.partners && filters.partners.length > 0;

    const isCampaignValid = Boolean(
      campaignData?.name?.trim() &&
        campaignData?.client?.trim() &&
        selectedDevices.length > 0
    );

    useImperativeHandle(ref, () => ({
      saveLoopData: async () => {
        const updatedData = {
          selectedDevices,
          filters,
          timeInterval,
        };
        onChange(updatedData);
        return updatedData;
      },
    }));

    const fetchOptions = useCallback(async (filterType) => {
      try {
        switch (filterType) {
          case "partners": {
            setLoadingFilters((prev) => ({ ...prev, partners: true }));
            const partnersResponse = await fetchPartners();
            setPartners(partnersResponse.data);
            setLoadingFilters((prev) => ({ ...prev, partners: false }));
            break;
          }
          case "tags": {
            setLoadingFilters((prev) => ({ ...prev, tags: true }));
            const tagsResponse = await fetchTags();
            setTags(tagsResponse.data);
            setLoadingFilters((prev) => ({ ...prev, tags: false }));
            break;
          }
          case "counties": {
            setLoadingFilters((prev) => ({ ...prev, counties: true }));
            const countiesResponse = await fetchCounties();
            setHierarchicalData((prev) => ({
              ...prev,
              counties: countiesResponse.data,
            }));
            setLoadingFilters((prev) => ({ ...prev, counties: false }));
            break;
          }
          case "cities": {
            setLoadingFilters((prev) => ({ ...prev, cities: true }));
            const citiesResponse = await fetchCities();
            setHierarchicalData((prev) => ({
              ...prev,
              cities: citiesResponse.data,
            }));
            setLoadingFilters((prev) => ({ ...prev, cities: false }));
            break;
          }
          case "locations": {
            setLoadingFilters((prev) => ({ ...prev, locations: true }));
            const locationsResponse = await fetchLocations();
            setHierarchicalData((prev) => ({
              ...prev,
              locations: locationsResponse.data,
            }));
            setLoadingFilters((prev) => ({ ...prev, locations: false }));
            break;
          }
          default:
            break;
        }
      } catch (error) {
        handleStatus(error.message, setToastStatus, setToastDescription);
      }
    }, []);

    const fetchFilteredDevices = async (
      pageNumber = 1,
      isLoading = true,
      updatedFilters = filters
    ) => {
      try {
        if (isLoading) setLoading(true);

        const response = await fetchDevices(updatedFilters, pageNumber);
        if (response.status !== "OK") {
          throw new Error(
            response.error || "Something went wrong, please try again later."
          );
        }

        setTotalCount(response.totalCount);
        setDevices(response.data);

        setSelectedDevices((prevSelected) => {
          const newDeviceIds = response.data.map((d) => d.device_id);
          return prevSelected.filter((sel) =>
            newDeviceIds.includes(sel.device_id)
          );
        });
      } catch (error) {
        handleStatus(error.message, setToastStatus, setToastDescription);
      } finally {
        if (isLoading) setLoading(false);
      }
    };

    const updateFilters = (newFilter) => {
      const updated = { ...filters, ...newFilter };
      setFilters(updated);
      fetchFilteredDevices(1, true, updated);
    };

    const getNetworkOptions = () => {
      const uniqueNetworks = [...new Set(partners.map((p) => p.network))];
      return uniqueNetworks.filter((n) => !!n);
    };

    const getFilteredPartners = () => {
      if (!hasNetworkSelected) return partners;
      return partners.filter((p) => filters.networks.includes(p.network));
    };

    const handleNetworkChange = (event, selectedNetworks) => {
      const reset = selectedNetworks.length === 0;

      updateFilters({
        networks: selectedNetworks,
        partners: [],
        counties: [],
        cities: [],
        locations: [],
      });
    };

    const handlePartnerChange = (event, selectedPartners) => {
      const partnerIds = selectedPartners.map((partner) => partner.ID);
      updateFilters({ partners: partnerIds });
    };

    const handleCountyChange = (event, selectedCounties) => {
      const countyNames = selectedCounties.map((c) => c);
      updateFilters({ counties: countyNames });
    };
    const handleCityChange = (event, selectedCities) => {
      const cityNames = selectedCities.map((c) => c);
      updateFilters({ cities: cityNames });
    };
    const handleLocationChange = (event, selectedLocations) => {
      const locationIds = selectedLocations.map((loc) => loc.ID);
      updateFilters({ locations: locationIds });
    };
    const handleTagChange = (event, selectedTags) => {
      const tagIds = selectedTags.map((t) => t.ID);
      updateFilters({ tags: tagIds });
    };

    const handleDeviceSelect = (deviceId, deviceName, checked) => {
      if (checked) {
        setSelectedDevices((prev) => [
          ...prev,
          { device_id: deviceId, name: deviceName },
        ]);
      } else {
        setSelectedDevices((prev) =>
          prev.filter((dev) => dev.device_id !== deviceId)
        );
      }
    };

    const handleChangePage = async (event, newPage) => {
      const updatedFilters = { ...filters, search: debouncedSearchTerm };
      await fetchFilteredDevices(newPage + 1, true, updatedFilters);
      setPage(newPage);
      setFilters(updatedFilters);
    };

    const handleSaveAsDraft = async () => {
      const updatedLoopData = ref.current
        ? await ref.current.saveLoopData()
        : {};

      if (onSubmitAll) {
        onSubmitAll(updatedLoopData, true); // Pass true to indicate this is a draft
      }
    };

    const handleSaveAndSubmit = async () => {
      if (!isCampaignValid) {
        handleStatus(
          "Campaign name and client are required before saving the loop.",
          setToastStatus,
          setToastDescription
        );
        return;
      }

      const updatedLoopData = ref.current
        ? await ref.current.saveLoopData()
        : {};

      if (onSubmitAll) {
        onSubmitAll(updatedLoopData, false); // Pass false to indicate this is not a draft
      }
    };

    const handleSelectAllDevices = () => {
      const allDevices = devices.map((dev) => ({
        device_id: dev.device_id,
        name: dev.name,
      }));

      setSelectedDevices(allDevices);
    };

    const handleDeselectAllDevices = () => {
      setSelectedDevices([]);
    };

    const handleSelectAllNetworks = () => {
      const allNetworks = getNetworkOptions();
      updateFilters({
        networks: allNetworks,
        partners: [],
        counties: [],
        cities: [],
        locations: [],
      });
    };

    const handleSelectAllPartners = () => {
      const allPartnerIds = getFilteredPartners().map((partner) => partner.ID);
      updateFilters({
        partners: allPartnerIds,
      });
    };

    useEffect(() => {
      if (debouncedSearchTerm.length < 3 && debouncedSearchTerm.length > 0) {
        return;
      }
      const updated = { ...filters, search: debouncedSearchTerm };
      setPage(0);
      fetchFilteredDevices(1, true, updated);
      setFilters(updated);
    }, [debouncedSearchTerm]);

    useEffect(() => {
      fetchFilteredDevices(1, true, filters);
    }, []);

    useEffect(() => {
      fetchOptions("partners");
      fetchOptions("tags");
      fetchOptions("counties");
      fetchOptions("cities");
      fetchOptions("locations");
      fetchFilteredDevices(1, true, initialFilters);
    }, []);

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        {toastStatus && <PopSnackbar message={toastDescription} />}
        <div
          style={{ display: "flex", alignItems: "center", alignSelf: "center" }}
        >
          <TextField
            disabled={loadingbtn}
            sx={styles.select}
            style={{
              width: "7vw",
              marginBottom: 30,
            }}
            type="number"
            value={60}
            onChange={(e) => {
              const value = e.target.value;

              if (/^\d*$/.test(value)) {
                const numericValue = value === "" ? "" : parseInt(value, 10);

                if (numericValue === "" || numericValue <= 60) {
                  setTimeInterval(numericValue);
                }
              }
            }}
            inputProps={{
              min: 1,
              max: 60,
              step: 1,
              style: { textAlign: "center" },
            }}
            placeholder="Minutes"
          />
        </div>

        <div style={{ display: "flex", gap: 40 }}>
          <div
            style={{
              flex: "0 0 20%",
              minWidth: "200px",
              display: "flex",
              flexDirection: "column",
              rowGap: "30px",
              boxSizing: "border-box",
            }}
          >
            <FilterAutocomplete
              disabled={loadingbtn}
              label="Selecteaza reteaua"
              options={getNetworkOptions()}
              value={filters.networks || []}
              onChange={handleNetworkChange}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              loading={false}
              multiple={true}
            />
            <button
              type="button"
              className="add-video-button"
              disabled={loadingbtn}
              onClick={handleSelectAllNetworks}
              style={{
                fontSize: "12px",
                padding: "8px 12px",
                marginTop: "-20px",
                marginBottom: "10px",
                opacity: loadingbtn ? 0.6 : 1,
                cursor: loadingbtn ? "not-allowed" : "pointer",
              }}
            >
              Selecteaza toate retelele
            </button>

            <FilterAutocomplete
              disabled={loadingbtn}
              label="Selecteaza partenerul"
              options={getFilteredPartners()}
              value={
                getFilteredPartners().filter((p) =>
                  filters.partners?.includes(p.ID)
                ) || []
              }
              onChange={handlePartnerChange}
              getOptionLabel={(option) => option?.name}
              isOptionEqualToValue={(option, value) => option.ID === value.ID}
              loading={loadingFilters.partners}
              multiple={true}
            />
            <button
              type="button"
              className="add-video-button"
              disabled={loadingbtn}
              onClick={handleSelectAllPartners}
              style={{
                fontSize: "12px",
                padding: "8px 12px",
                marginTop: "-20px",
                marginBottom: "10px",
                opacity: loadingbtn ? 0.6 : 1,
                cursor: loadingbtn ? "not-allowed" : "pointer",
              }}
            >
              Selecteaza toti partenerii
            </button>

            <FilterAutocomplete
              disabled={loadingbtn}
              label="Selecteaza judetul"
              options={hierarchicalData.counties || []}
              value={filters.counties || []}
              onChange={handleCountyChange}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              loading={loadingFilters.counties}
            />
            <FilterAutocomplete
              disabled={loadingbtn}
              label="Selecteaza orasul"
              options={Object.values(hierarchicalData.cities) || []}
              value={filters.cities || []}
              onChange={handleCityChange}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              loading={loadingFilters.cities}
            />
            <FilterAutocomplete
              disabled={loadingbtn}
              label="Selecteaza locatia"
              options={Object.values(hierarchicalData.locations) || []}
              value={Object.values(hierarchicalData.locations).filter((loc) =>
                filters.locations?.includes(loc.ID)
              )}
              onChange={handleLocationChange}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.ID === value.ID}
              loading={loadingFilters.locations}
            />
            <FilterAutocomplete
              disabled={loadingbtn}
              label="Selecteaza tagul"
              options={tags}
              value={tags.filter((t) => filters.tags?.includes(t.ID))}
              onChange={handleTagChange}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.ID === value.ID}
              loading={loadingFilters.tags}
            />
          </div>

          <div style={{ flex: 2 }}>
            <CustomTable
              search={true}
              columns={columns}
              tableLoading={loading}
              inputText={inputText}
              setInputText={setInputText}
              data={devices}
              page={page}
              handleChangePage={handleChangePage}
              totalCount={totalCount}
              addNew={true}
              addNewTitle={"Selecteaza toate playerele"}
              addNewFunction={handleSelectAllDevices}
              secondAddNew={true}
              secondAddNewTitle={"Deselecteaza toate playerele"}
              secondAddNewFunction={handleDeselectAllDevices}
              disabled={loadingbtn}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            width: "100%",
            columnGap: 10,
            marginTop: 60,
            alignSelf: "center",
          }}
        >
          <button
            style={{
              opacity: loadingbtn ? 0.6 : 1,
              cursor: loadingbtn ? "not-allowed" : "pointer",
            }}
            type="button"
            className="add-video-button"
            disabled={loadingbtn}
            onClick={() => {
              if (ref?.current?.saveLoopData) {
                ref.current.saveLoopData();
              }
              goBack();
            }}
          >
            ← Inapoi la configurarea campaniei
          </button>

          <button
            type="button"
            className="add-video-button"
            onClick={handleSaveAsDraft}
            disabled={loadingbtn}
            style={{
              opacity: loadingbtn ? 0.6 : 1,
              cursor: loadingbtn ? "not-allowed" : "pointer",
              background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
              color: "white",
              marginRight: "10px"
            }}
          >
            {loadingbtn ? (
              <CircularProgress
                size={20}
                style={{ alignSelf: "center" }}
                color="black"
              />
            ) : (
              "Salveaza ca Draft"
            )}
          </button>

          <button
            type="button"
            className="add-video-button"
            onClick={handleSaveAndSubmit}
            disabled={!isCampaignValid || loadingbtn}
            style={{
              opacity: !isCampaignValid || loadingbtn ? 0.6 : 1,
              cursor:
                !isCampaignValid || loadingbtn ? "not-allowed" : "pointer",
            }}
          >
            {loadingbtn ? (
              <CircularProgress
                size={20}
                style={{ alignSelf: "center" }}
                color="black"
              />
            ) : (
              "Salveaza campania & Genereaza playlist →"
            )}
          </button>
        </div>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loopPreviewLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    );
  }
);

export default EditLoopStep;
